<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import AiCloserAPI from 'dashboard/api/aiCloser';

const props = defineProps({
  product: {
    type: Object,
    default: () => ({}),
  },
  assistantId: {
    type: [Number, String],
    default: null,
  },
});
const store = useStore();
const { t } = useI18n();
const BASE_KEY = 'AILAB.ASSISTANT_V2.PRODUCT_ANSWER_TEMPLATE';

const isSubmitting = ref(false);
const isLoading = ref(false);
const showAddModal = ref(false);
const editingItem = ref(null);
const lastSavedDraftSnapshot = ref('');
const pageLimit = 50;
const currentOffset = ref(0);
const totalCount = ref(0);
const searchQuery = ref('');
const selectedListLanguage = ref(''); // '' means all
const selectedListTag = ref(''); // '' means all (question prefix tag)
const selectedCategory = ref(''); // '' means all; '__uncategorized__' shows blanks
const showCompatibilityOnly = ref(false);

const assistantRecord = computed(() =>
  store.getters?.['ailabAssistants/getRecord']
    ? store.getters['ailabAssistants/getRecord'](Number(props.assistantId))
    : null
);

const isAIGamer = computed(
  () => assistantRecord.value?.config?.wlab_sidebar_key === 'ai_gamer'
);

const LANGUAGE_CODES = ['th', 'en', 'ms', 'zh', 'id', 'vi', 'ta'];

// Follow General preferred language for default selection + ordering.
const aiCloserConfig = computed(() => store.getters['aiCloser/getConfig']);
const preferredLanguageCode = computed(() => {
  const v = (aiCloserConfig.value?.preferred_language || '')
    .toString()
    .trim()
    .toLowerCase();
  if (v === 'thai') return 'th';
  if (v === 'english') return 'en';
  if (v === 'malay') return 'ms';
  if (v === 'chinese') return 'zh';
  if (v === 'indonesian') return 'id';
  if (v === 'vietnamese') return 'vi';
  if (v === 'tamil') return 'ta';
  return 'th';
});

const answerLanguages = computed(() => {
  const pref = preferredLanguageCode.value || 'th';
  const list = LANGUAGE_CODES.map(code => ({
    value: code,
    label: t(
      `AILAB.ASSISTANT_V2.PRODUCT_ANSWER_TEMPLATE.LANGUAGES.${code.toUpperCase()}`
    ),
  }));
  const head = list.find(l => l.value === pref);
  const tail = list.filter(l => l.value !== pref);
  return head ? [head, ...tail] : list;
});
const selectedLanguage = ref('th');

const faqTags = [
  'PROMO_TERMS',
  'TURNOVER',
  'ELIGIBILITY',
  'WITHDRAW',
  'DEPOSIT',
  'CANNOT_CLAIM',
  'DISQUALIFY',
  'PROVIDERS',
  'COMMON_ISSUES',
  'CONTACT',
];

const selectedTag = ref('COMMON_ISSUES');

const TAG_TO_CATEGORY = {
  WITHDRAW: 'withdraw',
  DEPOSIT: 'deposit',
  TURNOVER: 'turnover',
  PROMO_TERMS: 'promo',
  ELIGIBILITY: 'promo',
  CANNOT_CLAIM: 'promo',
  DISQUALIFY: 'promo',
  PROVIDERS: 'game',
  CONTACT: 'entry',
  COMMON_ISSUES: 'general',
};

const AI_GAMER_CATEGORIES = [
  { value: 'promo', label: 'promo' },
  { value: 'deposit', label: 'deposit' },
  { value: 'withdraw', label: 'withdraw' },
  { value: 'account', label: 'account/login/otp' },
  { value: 'entry', label: 'entry/link/domain' },
  { value: 'kyc', label: 'kyc/verify' },
  { value: 'turnover', label: 'turnover' },
  { value: 'complaint', label: 'complaint/profanity' },
  { value: 'casino_faq', label: 'casino_faq/rules' },
  { value: 'game', label: 'game/providers' },
  { value: 'general', label: 'general' },
];

const currentQuestionTag = computed(() => {
  const match = (newItem.question || '').match(/^\[([A-Z0-9_]+)\]\s/);
  if (!match) return '';
  return match[1] || '';
});

const canReplaceTag = computed(() => {
  if (!isAIGamer.value) return false;
  if (!currentQuestionTag.value) return false;
  if (!selectedTag.value) return false;
  return currentQuestionTag.value !== selectedTag.value;
});

const ensureTagPrefix = () => {
  if (!isAIGamer.value) return;
  const tag = selectedTag.value;
  if (!tag) return;
  const prefix = `[${tag}] `;
  if (!newItem.question?.startsWith(prefix)) {
    // If another tag exists, keep it (avoid double tagging).
    if (/^\[[A-Z0-9_]+\]\s/.test(newItem.question || '')) return;
    newItem.question = `${prefix}${newItem.question || ''}`.trimEnd();
  }
  const mapped = TAG_TO_CATEGORY?.[tag];
  if (!newItem.category && mapped) newItem.category = mapped;
};

const replaceTagPrefix = () => {
  if (!canReplaceTag.value) return;
  const tag = selectedTag.value;
  const rest = (newItem.question || '').replace(/^\[[A-Z0-9_]+\]\s*/, '');
  newItem.question = `[${tag}] ${rest}`.trimEnd();
  const mapped = TAG_TO_CATEGORY?.[tag];
  if (mapped) newItem.category = mapped;
};

const insertAnswerTemplate = lang => {
  const template = lang === 'th' ? answerSkeletonTH() : answerSkeletonEN();
  if (!template) return;

  // If empty, just insert.
  if (!newItem.answer) {
    newItem.answer = template;
    return;
  }

  // If not empty, ask user whether to replace or append.
  const shouldReplace = window.confirm(
    t(`${BASE_KEY}.CONFIRMS.ANSWER_REPLACE_OR_APPEND`)
  );

  if (shouldReplace) {
    newItem.answer = template;
  } else {
    newItem.answer = `${newItem.answer}\n\n${template}`;
  }
};

const answerSkeletonTH = () => {
  return [
    'สรุป: ',
    'เงื่อนไข: ',
    'ขั้นตอน: ',
    'หมายเหตุ: 18+ • เล่นเพื่อความบันเทิงเท่านั้น • ไม่การันตีผล • โปรเปลี่ยนได้ตามประกาศ',
    'ติดต่อแอดมิน: หากยังไม่ได้ กรุณาทักแอดมินพร้อมข้อมูล/สลิปที่เกี่ยวข้อง',
  ].join('\n');
};

const answerSkeletonEN = () => {
  return [
    'Summary: ',
    'Terms: ',
    'Steps: ',
    'Compliance: 18+ • Entertainment only • No guaranteed wins • Terms may change',
    'Contact admin: If the issue persists, contact admin with required details/screenshots.',
  ].join('\n');
};

// Get answer templates from store
const storeTemplates = computed(
  () => store.getters['aiCloser/getAnswerTemplates']
);
const storeTemplatesMeta = computed(
  () => store.getters['aiCloser/getAnswerTemplatesMeta']
);

// Local reactive Q&A data
const qaItems = reactive([]);

// New item form
const newItem = reactive({
  question: '',
  answer: '',
  category: '',
  language: 'th',
  enabled: true,
  priority: 0,
});

const snapshotDraft = () =>
  JSON.stringify({
    question: newItem.question || '',
    answer: newItem.answer || '',
    category: newItem.category || '',
    language: newItem.language || '',
    enabled: newItem.enabled !== false,
    priority: Number(newItem.priority || 0),
    selectedLanguage: selectedLanguage.value || '',
    selectedTag: selectedTag.value || '',
  });

const markDraftSnapshot = () => {
  lastSavedDraftSnapshot.value = snapshotDraft();
};

const syncTemplatesFromStore = items => {
  qaItems.length = 0;
  (items || []).forEach(t => {
    qaItems.push({
      id: t.id,
      question: t.question || '',
      answer: t.answer || '',
      category: t.category || '',
      language: (t.language || '').toLowerCase() || '',
      enabled: t.enabled !== false,
      priority: Number(t.priority || 0),
      shadowedByPublishedArticle: t.shadowed_by_published_article === true,
      canonicalArticleId: t.canonical_article_id || null,
    });
  });
};

const fetchPage = async ({ reset = false } = {}) => {
  if (!props.assistantId) return;
  isLoading.value = true;
  try {
    const nextOffset = reset ? 0 : currentOffset.value;
    const params = { limit: pageLimit, offset: nextOffset };
    if (searchQuery.value?.trim()) params.q = searchQuery.value.trim();
    if (selectedListLanguage.value)
      params.language = selectedListLanguage.value;
    if (selectedListTag.value) params.tag = selectedListTag.value;
    if (selectedCategory.value) params.category = selectedCategory.value;
    if (showCompatibilityOnly.value) params.compatibility_only = true;

    const items = await store.dispatch('aiCloser/fetchAnswerTemplates', {
      assistantId: props.assistantId,
      params,
      append: !reset && nextOffset > 0,
    });

    syncTemplatesFromStore(
      items || store.getters['aiCloser/getAnswerTemplates']
    );
    const meta = store.getters['aiCloser/getAnswerTemplatesMeta'];
    totalCount.value = meta?.total ?? qaItems.length;

    currentOffset.value = nextOffset;
  } catch (error) {
    // Templates may not exist yet
  } finally {
    isLoading.value = false;
  }
};

// Fetch templates on mount
onMounted(async () => {
  if (!props.assistantId) return;
  try {
    await store.dispatch('aiCloser/fetchConfig', {
      assistantId: props.assistantId,
    });
  } catch (e) {
    // ignore
  }
  await fetchPage({ reset: true });
});

// Also sync from store as fallback when store changes externally.
watch(
  () => storeTemplates.value,
  newVal => {
    syncTemplatesFromStore(newVal);
  },
  { immediate: true, deep: true }
);

watch(
  () => storeTemplatesMeta.value,
  meta => {
    totalCount.value = meta?.total ?? qaItems.length;
  },
  { immediate: true, deep: true }
);

const canLoadMore = computed(() => qaItems.length < (totalCount.value || 0));
const shadowedTemplatesCount = computed(
  () =>
    storeTemplatesMeta.value?.shadowed_published_count ??
    qaItems.filter(item => item.shadowedByPublishedArticle).length
);
const compatibilityTemplatesCount = computed(
  () =>
    storeTemplatesMeta.value?.compatibility_only_count ??
    qaItems.length - shadowedTemplatesCount.value
);
const displayedItems = computed(() => qaItems);

const handleSearch = async () => {
  currentOffset.value = 0;
  await fetchPage({ reset: true });
};

const handleLoadMore = async () => {
  if (!canLoadMore.value) return;
  currentOffset.value += pageLimit;
  await fetchPage({ reset: false });
};

const handleDisableShadowed = async () => {
  if (!props.assistantId || shadowedTemplatesCount.value <= 0) return;

  const confirmed = window.confirm(
    `Disable ${shadowedTemplatesCount.value} shadowed legacy templates that are already live as canonical articles?`
  );
  if (!confirmed) return;

  try {
    const response = await AiCloserAPI.disableShadowedAnswerTemplates(
      props.assistantId
    );
    currentOffset.value = 0;
    await fetchPage({ reset: true });
    useAlert(
      response?.data?.message ||
        'Disabled shadowed legacy templates successfully.'
    );
  } catch (error) {
    const serverMsg =
      error?.response?.data?.error ||
      (Array.isArray(error?.response?.data?.errors)
        ? error.response.data.errors.join(', ')
        : null);
    useAlert(serverMsg || error?.message || 'Failed to disable shadowed templates.');
  }
};

watch(
  () => showCompatibilityOnly.value,
  async () => {
    currentOffset.value = 0;
    await fetchPage({ reset: true });
  }
);

const handleImportCSV = async () => {
  if (!props.assistantId) return;
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.csv';
  input.onchange = async e => {
    const file = e.target.files[0];
    if (file) {
      try {
        useAlert(
          t(`${BASE_KEY}.ALERTS.IMPORTING_FILE`, { filename: file.name })
        );
        await AiCloserAPI.importAnswerTemplatesCSV(props.assistantId, file);
        currentOffset.value = 0;
        await fetchPage({ reset: true });
        useAlert(t(`${BASE_KEY}.ALERTS.IMPORT_COMPLETED`));
      } catch (error) {
        const serverMsg =
          error?.response?.data?.error ||
          (Array.isArray(error?.response?.data?.errors)
            ? error.response.data.errors.join(', ')
            : null);
        useAlert(
          serverMsg || error?.message || t(`${BASE_KEY}.ALERTS.IMPORT_FAILED`)
        );
      }
    }
  };
  input.click();
};

const handleImportEvalCasesCSV = async () => {
  if (!props.assistantId) return;
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.csv';
  input.onchange = async e => {
    const file = e.target.files[0];
    if (file) {
      try {
        // Import controls (brand-agnostic) to speed up reaching high overlap scores
        const topNRaw = window.prompt(t(`${BASE_KEY}.PROMPTS.TOP_N`), '500');
        const topN = topNRaw ? Number(topNRaw) : 0;
        const minCountRaw = window.prompt(
          t(`${BASE_KEY}.PROMPTS.MIN_COUNT`),
          '2'
        );
        const minCount = minCountRaw ? Number(minCountRaw) : 0;
        const onlyFailed = window.confirm(
          t(`${BASE_KEY}.CONFIRMS.ONLY_FAILED_CASES`)
        );
        const priorityMode =
          window.prompt(t(`${BASE_KEY}.PROMPTS.PRIORITY_MODE`), 'by_count') ||
          'by_count';

        useAlert(
          t(`${BASE_KEY}.ALERTS.IMPORTING_EVAL_CASES`, { filename: file.name })
        );
        await AiCloserAPI.importAnswerTemplatesEvalCasesCSV(
          props.assistantId,
          file,
          {
            top_n:
              Number.isFinite(topN) && topN > 0 ? String(Math.floor(topN)) : '',
            min_count:
              Number.isFinite(minCount) && minCount > 0
                ? String(Math.floor(minCount))
                : '',
            only_failed: onlyFailed ? 'true' : 'false',
            priority_mode: priorityMode,
          }
        );
        currentOffset.value = 0;
        await fetchPage({ reset: true });
        useAlert(t(`${BASE_KEY}.ALERTS.IMPORT_COMPLETED`));
      } catch (error) {
        const serverMsg =
          error?.response?.data?.error ||
          (Array.isArray(error?.response?.data?.errors)
            ? error.response.data.errors.join(', ')
            : null);
        useAlert(
          serverMsg || error?.message || t(`${BASE_KEY}.ALERTS.IMPORT_FAILED`)
        );
      }
    }
  };
  input.click();
};

const handleExportCSV = async () => {
  if (!props.assistantId) return;
  try {
    useAlert(t(`${BASE_KEY}.ALERTS.EXPORTING_CSV`));
    const response = await AiCloserAPI.exportAnswerTemplatesCSV(
      props.assistantId
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'answer_templates.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    useAlert(t(`${BASE_KEY}.ALERTS.EXPORT_COMPLETED`));
  } catch (error) {
    useAlert(error?.message || t(`${BASE_KEY}.ALERTS.EXPORT_FAILED`));
  }
};

const handleAdd = () => {
  showAddModal.value = true;
  editingItem.value = null;
  newItem.question = '';
  newItem.answer = '';
  newItem.category = '';
  newItem.language = preferredLanguageCode.value || 'th';
  newItem.enabled = true;
  newItem.priority = 0;
  selectedLanguage.value = newItem.language;
  selectedTag.value = 'COMMON_ISSUES';
  markDraftSnapshot();
};

const handleEdit = item => {
  editingItem.value = item;
  newItem.question = item.question;
  newItem.answer = item.answer;
  newItem.category = item.category || '';
  newItem.language = (item.language || '').toLowerCase() || 'th';
  newItem.enabled = item.enabled !== false;
  newItem.priority = Number(item.priority || 0);
  selectedLanguage.value = newItem.language;
  showAddModal.value = true;
  // Try to infer tag from question prefix if any.
  const tag = currentQuestionTag.value;
  if (tag && faqTags.includes(tag)) {
    selectedTag.value = tag;
  }
  markDraftSnapshot();
};

const showDeleteConfirmModal = ref(false);
const deletingId = ref(null);

const openDeleteConfirm = id => {
  deletingId.value = id;
  showDeleteConfirmModal.value = true;
};

const closeDeleteConfirm = () => {
  showDeleteConfirmModal.value = false;
  deletingId.value = null;
};

const handleDelete = async id => {
  openDeleteConfirm(id);
};

const confirmDelete = async () => {
  const id = Number(deletingId.value);
  if (!props.assistantId) return;
  if (!id) {
    closeDeleteConfirm();
    return;
  }
  try {
    await store.dispatch('aiCloser/deleteAnswerTemplate', {
      assistantId: props.assistantId,
      templateId: id,
    });
    useAlert(t(`${BASE_KEY}.ALERTS.ITEM_DELETED`));
    currentOffset.value = 0;
    await fetchPage({ reset: true });
    closeDeleteConfirm();
  } catch (error) {
    useAlert(error?.message || t(`${BASE_KEY}.ALERTS.DELETE_FAILED`));
  }
};

const handleSave = async () => {
  if (!props.assistantId) return;
  // Ensure tag prefix is applied before submit (AI gamer only).
  if (isAIGamer.value) {
    ensureTagPrefix();
    newItem.language = selectedLanguage.value || 'th';
    if (canReplaceTag.value) {
      useAlert(
        t(`${BASE_KEY}.ALERTS.TAG_DIFFERS`, {
          action: t(`${BASE_KEY}.ACTIONS.REPLACE_TAG`),
        })
      );
    }
  }
  if (!newItem.question || !newItem.answer) {
    useAlert(t(`${BASE_KEY}.ALERTS.QUESTION_ANSWER_REQUIRED`));
    return;
  }
  if (isAIGamer.value && !newItem.category) {
    const key = (currentQuestionTag.value || selectedTag.value || '')
      .toString()
      .trim()
      .toUpperCase();
    const mapped = TAG_TO_CATEGORY?.[key];
    if (mapped) newItem.category = mapped;
  }

  isSubmitting.value = true;
  try {
    if (editingItem.value) {
      // Update existing
      await store.dispatch('aiCloser/updateAnswerTemplate', {
        assistantId: props.assistantId,
        templateId: editingItem.value.id,
        data: {
          question: newItem.question,
          answer: newItem.answer,
          category: newItem.category,
          language: isAIGamer.value ? newItem.language || 'th' : undefined,
          enabled: isAIGamer.value ? newItem.enabled : undefined,
          priority: isAIGamer.value ? Number(newItem.priority || 0) : undefined,
        },
      });
      useAlert(t(`${BASE_KEY}.ALERTS.ITEM_UPDATED`));
    } else {
      // Add new
      await store.dispatch('aiCloser/createAnswerTemplate', {
        assistantId: props.assistantId,
        data: {
          question: newItem.question,
          answer: newItem.answer,
          category: newItem.category,
          language: isAIGamer.value ? newItem.language || 'th' : undefined,
          enabled: isAIGamer.value ? newItem.enabled : undefined,
          priority: isAIGamer.value ? Number(newItem.priority || 0) : undefined,
        },
      });
      useAlert(t(`${BASE_KEY}.ALERTS.ITEM_ADDED`));
    }
    markDraftSnapshot();
    showAddModal.value = false;
    currentOffset.value = 0;
    await fetchPage({ reset: true });
  } catch (error) {
    useAlert(error?.message || t(`${BASE_KEY}.ALERTS.SAVE_FAILED`));
  } finally {
    isSubmitting.value = false;
  }
};

const handleCancel = () => {
  showAddModal.value = false;
};

const canSaveDraft = computed(
  () =>
    Boolean(
      props.assistantId &&
        showAddModal.value &&
        !isSubmitting.value &&
        String(newItem.question || '').trim() &&
        String(newItem.answer || '').trim() &&
        snapshotDraft() !== lastSavedDraftSnapshot.value
    )
);

const autoTagUncategorized = async () => {
  if (!props.assistantId) return;
  if (!isAIGamer.value) return;
  const ok = window.confirm(t(`${BASE_KEY}.CONFIRMS.AUTO_TAG_UNCATEGORIZED`));
  if (!ok) return;
  isLoading.value = true;
  try {
    const res = await store.dispatch('aiCloser/autoTagAnswerTemplates', {
      assistantId: props.assistantId,
      options: { only_uncategorized: true },
    });
    useAlert(
      t(`${BASE_KEY}.ALERTS.AUTO_TAG_COMPLETED`, { count: res?.updated ?? 0 })
    );
    currentOffset.value = 0;
    await fetchPage({ reset: true });
  } catch (e) {
    useAlert(e?.message || t(`${BASE_KEY}.ALERTS.AUTO_TAG_FAILED`));
  } finally {
    isLoading.value = false;
  }
};

defineExpose({
  save: handleSave,
  canSave: () => canSaveDraft.value,
});
</script>

<template>
  <div class="answer-template-tab">
    <!-- Toolbar -->
    <div class="toolbar">
      <div class="toolbar-left">
        <button
          v-if="isAIGamer"
          class="import-btn"
          @click="handleImportEvalCasesCSV"
        >
          <span class="i-lucide-file-up size-4" />
          {{ t(`${BASE_KEY}.TOOLBAR.IMPORT_EVAL_CASES`) }}
        </button>
        <button class="import-btn" @click="handleImportCSV">
          <span class="i-lucide-file-up size-4" />
          {{
            isAIGamer
              ? t(`${BASE_KEY}.TOOLBAR.IMPORT_FAQ_CSV`)
              : t(`${BASE_KEY}.TOOLBAR.IMPORT_CSV`)
          }}
        </button>
        <button class="export-btn" @click="handleExportCSV">
          <span class="i-lucide-file-down size-4" />
          {{
            isAIGamer
              ? t(`${BASE_KEY}.TOOLBAR.EXPORT_FAQ_CSV`)
              : t(`${BASE_KEY}.TOOLBAR.EXPORT_CSV`)
          }}
        </button>
        <button
          v-if="isAIGamer"
          class="import-btn"
          @click="autoTagUncategorized"
        >
          <span class="i-lucide-wand-2 size-4" />
          {{ t(`${BASE_KEY}.TOOLBAR.AUTO_TAG_UNCATEGORIZED`) }}
        </button>
      </div>
      <div class="toolbar-right">
        <span class="count-badge">{{
          t(`${BASE_KEY}.META.COUNT`, {
            shown: displayedItems.length,
            total: totalCount,
          })
        }}</span>
      </div>
    </div>

    <div class="toolbar" style="padding: 12px 16px">
      <div class="toolbar-left" style="gap: 8px; flex-wrap: wrap">
        <input
          v-model="searchQuery"
          class="tag-select"
          type="text"
          :placeholder="t(`${BASE_KEY}.FILTERS.SEARCH_PLACEHOLDER`)"
          @keyup.enter="handleSearch"
        />
        <select
          v-if="isAIGamer"
          v-model="selectedListLanguage"
          class="tag-select"
          @change="handleSearch"
        >
          <option value="">{{ t(`${BASE_KEY}.FILTERS.ALL_LANGUAGES`) }}</option>
          <option
            v-for="opt in answerLanguages"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
        <select
          v-if="isAIGamer"
          v-model="selectedListTag"
          class="tag-select"
          @change="handleSearch"
        >
          <option value="">
            {{ t(`${BASE_KEY}.FILTERS.ALL_QUESTION_TAGS`) }}
          </option>
          <option v-for="t in faqTags" :key="t" :value="t">{{ t }}</option>
        </select>
        <select
          v-if="isAIGamer"
          v-model="selectedCategory"
          class="tag-select"
          @change="handleSearch"
        >
          <option value="">
            {{ t(`${BASE_KEY}.FILTERS.ALL_CATEGORIES`) }}
          </option>
          <option value="__uncategorized__">
            {{ t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FOLDER.UNCATEGORIZED') }}
          </option>
          <option
            v-for="c in AI_GAMER_CATEGORIES"
            :key="c.value"
            :value="c.value"
          >
            {{ c.label }}
          </option>
        </select>
        <button
          class="tag-apply-btn"
          type="button"
          :disabled="isLoading"
          @click="handleSearch"
        >
          <span
            v-if="isLoading"
            class="i-lucide-loader-2 size-4 animate-spin"
          />
          {{ t(`${BASE_KEY}.ACTIONS.SEARCH`) }}
        </button>
      </div>
      <div class="toolbar-right">
        <button
          v-if="canLoadMore"
          class="tag-apply-btn"
          type="button"
          :disabled="isLoading"
          @click="handleLoadMore"
        >
          {{ t(`${BASE_KEY}.ACTIONS.LOAD_MORE`) }}
        </button>
      </div>
    </div>

    <div class="legacy-notice">
      <div class="legacy-notice-title">Legacy compatibility surface</div>
      <p class="legacy-notice-text">
        Published canonical knowledge now lives in `Train Content` as Help Center
        articles. This page remains available for legacy template maintenance,
        import/export, and backward compatibility during migration.
      </p>
      <div class="legacy-notice-meta">
        <span>{{ compatibilityTemplatesCount }} compatibility items on this page</span>
        <span>{{ shadowedTemplatesCount }} already live as canonical articles</span>
      </div>
    </div>

    <div class="compat-toolbar">
      <label class="compat-toggle">
        <input v-model="showCompatibilityOnly" type="checkbox" />
        <span>Show compatibility-only items</span>
      </label>
      <button
        v-if="shadowedTemplatesCount > 0"
        class="compat-action-btn"
        type="button"
        :disabled="isLoading"
        @click="handleDisableShadowed"
      >
        Disable shadowed legacy templates
      </button>
    </div>

    <!-- AI gamer guide -->
    <div v-if="isAIGamer" class="faq-guide">
      <div class="faq-guide-title">{{ t(`${BASE_KEY}.GUIDE.TITLE`) }}</div>
      <ul class="faq-guide-list">
        <li>
          {{ t(`${BASE_KEY}.GUIDE.BULLETS.START_QUESTION_PREFIX`) }}
          <b>{{ '[WITHDRAW]' }}</b
          >, <b>{{ '[DEPOSIT]' }}</b
          >, <b>{{ '[TURNOVER]' }}</b
          >, <b>{{ '[PROMO_TERMS]' }}</b
          >…
        </li>
        <li>
          {{ t(`${BASE_KEY}.GUIDE.BULLETS.ANSWER_FORMAT_PREFIX`) }}
          <b>{{ t(`${BASE_KEY}.GUIDE.BULLETS.ANSWER_FORMAT_BOLD`) }}</b>
        </li>
        <li>
          {{ t(`${BASE_KEY}.GUIDE.BULLETS.COMPLIANCE_PREFIX`) }} <b>18+</b> •
          <b>{{ t(`${BASE_KEY}.GUIDE.BULLETS.NO_GUARANTEED_WINS`) }}</b> •
          <b>{{ t(`${BASE_KEY}.GUIDE.BULLETS.TERMS_MAY_CHANGE`) }}</b>
        </li>
      </ul>
    </div>

    <!-- Q&A Table -->
    <div class="qa-container">
      <table class="qa-table">
        <thead>
          <tr>
            <th class="col-question">
              {{ t(`${BASE_KEY}.TABLE.HEADERS.QUESTION`) }}
            </th>
            <th class="col-answer">
              {{ t(`${BASE_KEY}.TABLE.HEADERS.ANSWER`) }}
            </th>
            <th class="col-action">
              {{ t(`${BASE_KEY}.TABLE.HEADERS.ACTION`) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in displayedItems" :key="item.id">
            <td class="col-question">
              <div class="question-bubble">
                <p>{{ item.question }}</p>
              </div>
              <div v-if="isAIGamer" class="mt-1 flex items-center gap-2">
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs"
                  :class="
                    item.enabled
                      ? 'bg-green-100 text-green-800'
                      : 'bg-slate-200 text-slate-700'
                  "
                >
                  {{
                    item.enabled
                      ? t(`${BASE_KEY}.STATUS.ENABLED`)
                      : t(`${BASE_KEY}.STATUS.DISABLED`)
                  }}
                </span>
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs"
                  :class="
                    (item.category || '').trim()
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-red-100 text-red-800'
                  "
                  :title="
                    (item.category || '').trim()
                      ? t(`${BASE_KEY}.META.CATEGORY_TOOLTIP`)
                      : t(`${BASE_KEY}.META.MISSING_CATEGORY_TOOLTIP`)
                  "
                >
                  {{
                    (item.category || '').trim()
                      ? t(`${BASE_KEY}.META.CATEGORY_TAG`, {
                          category: item.category,
                        })
                      : t(`${BASE_KEY}.META.CATEGORY_MISSING`)
                  }}
                </span>
                <span class="text-xs text-slate-500">{{
                  t(`${BASE_KEY}.META.PRIORITY`, { priority: item.priority })
                }}</span>
                <span
                  v-if="item.shadowedByPublishedArticle"
                  class="compat-badge"
                  :title="`Canonical article #${item.canonicalArticleId || 'live'}`"
                >
                  Canonical article live
                </span>
              </div>
            </td>
            <td class="col-answer">
              <div class="answer-bubble">
                <p>{{ item.answer }}</p>
              </div>
            </td>
            <td class="col-action">
              <div class="action-btns">
                <button class="action-btn edit" @click="handleEdit(item)">
                  <span class="i-lucide-pencil size-4" />
                </button>
                <button
                  class="action-btn delete"
                  @click="handleDelete(item.id)"
                >
                  <span class="i-lucide-trash-2 size-4" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Empty State -->
      <div v-if="displayedItems.length === 0" class="empty-state">
        <span class="i-lucide-message-square-quote size-12 text-white/20" />
        <p>{{
          showCompatibilityOnly
            ? 'No compatibility-only templates are visible on this page.'
            : t(`${BASE_KEY}.EMPTY.TITLE`)
        }}</p>
        <button class="add-first-btn" @click="handleAdd">
          <span class="i-lucide-plus size-4" />
          {{ t(`${BASE_KEY}.EMPTY.ACTION`) }}
        </button>
      </div>
    </div>

    <!-- FAB Button -->
    <button class="fab-btn" @click="handleAdd">
      <span class="i-lucide-plus size-5" />
    </button>

    <!-- Add/Edit Modal -->
    <Teleport v-if="showAddModal" to="body">
      <div class="modal-overlay" @click.self="handleCancel">
        <div class="modal-content">
          <div class="modal-header">
            <h3>
              {{
                editingItem
                  ? t(`${BASE_KEY}.MODAL.EDIT_TITLE`)
                  : t(`${BASE_KEY}.MODAL.ADD_TITLE`)
              }}
            </h3>
            <button class="close-btn" @click="handleCancel">
              <span class="i-lucide-x size-5" />
            </button>
          </div>
          <div class="modal-body modal-body--wide">
            <div class="modal-grid">
              <div class="modal-main">
                <div class="form-group">
                  <label>{{
                    isAIGamer
                      ? t(`${BASE_KEY}.FIELDS.QUESTION.LABEL_AIGAMER`)
                      : t(`${BASE_KEY}.FIELDS.QUESTION.LABEL_DEFAULT`)
                  }}</label>
                  <textarea
                    v-model="newItem.question"
                    class="form-textarea"
                    :placeholder="
                      isAIGamer
                        ? t(`${BASE_KEY}.FIELDS.QUESTION.PLACEHOLDER_AIGAMER`)
                        : t(`${BASE_KEY}.FIELDS.QUESTION.PLACEHOLDER_DEFAULT`)
                    "
                    rows="3"
                    @blur="ensureTagPrefix"
                  />
                  <p v-if="isAIGamer" class="field-help">
                    {{ t(`${BASE_KEY}.GUIDE.RECOMMENDED_TAGS_PREFIX`) }}
                    [PROMO_TERMS], [TURNOVER], [ELIGIBILITY], [WITHDRAW],
                    [DEPOSIT], [CANNOT_CLAIM], [DISQUALIFY], [PROVIDERS],
                    [COMMON_ISSUES], [CONTACT]
                  </p>
                </div>

                <div class="form-group">
                  <label>{{
                    isAIGamer
                      ? t(`${BASE_KEY}.FIELDS.ANSWER.LABEL_AIGAMER`)
                      : t(`${BASE_KEY}.FIELDS.ANSWER.LABEL_DEFAULT`)
                  }}</label>
                  <textarea
                    v-model="newItem.answer"
                    class="form-textarea"
                    :placeholder="
                      isAIGamer
                        ? t(`${BASE_KEY}.FIELDS.ANSWER.PLACEHOLDER_AIGAMER`)
                        : t(`${BASE_KEY}.FIELDS.ANSWER.PLACEHOLDER_DEFAULT`)
                    "
                    rows="12"
                  />
                  <p v-if="isAIGamer" class="field-help">
                    {{ t(`${BASE_KEY}.GUIDE.ANSWER_CONSISTENCY_NOTE`) }}
                  </p>
                </div>
              </div>

              <div class="modal-side">
                <div v-if="isAIGamer" class="form-group">
                  <label>{{ t(`${BASE_KEY}.FIELDS.LANGUAGE.LABEL`) }}</label>
                  <select v-model="selectedLanguage" class="tag-select">
                    <option
                      v-for="opt in answerLanguages"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                  <p class="field-help">
                    {{ t(`${BASE_KEY}.FIELDS.LANGUAGE.HELP`) }}
                  </p>
                </div>

                <div v-if="isAIGamer" class="form-group">
                  <label>{{
                    t(`${BASE_KEY}.FIELDS.STATUS_PRIORITY.LABEL`)
                  }}</label>
                  <div class="flex items-center gap-3 flex-wrap">
                    <label class="inline-flex items-center gap-2">
                      <input
                        v-model="newItem.enabled"
                        type="checkbox"
                        class="h-4 w-4"
                      />
                      <span>{{ t(`${BASE_KEY}.STATUS.ENABLED`) }}</span>
                    </label>
                    <div class="flex items-center gap-2">
                      <span class="text-sm">{{
                        t(`${BASE_KEY}.FIELDS.PRIORITY.LABEL`)
                      }}</span>
                      <input
                        v-model.number="newItem.priority"
                        type="number"
                        class="tag-select"
                        style="width: 140px"
                        min="-100"
                        max="100"
                        step="1"
                      />
                    </div>
                  </div>
                  <p class="field-help">
                    {{ t(`${BASE_KEY}.FIELDS.PRIORITY.HELP`) }}
                  </p>
                </div>

                <div v-if="isAIGamer" class="form-group">
                  <label class="tag-label">{{
                    t(`${BASE_KEY}.FIELDS.TAG.LABEL`)
                  }}</label>
                  <div class="flex flex-wrap items-center gap-2">
                    <select
                      v-model="selectedTag"
                      class="tag-select"
                      @change="ensureTagPrefix"
                    >
                      <option v-for="t in faqTags" :key="t" :value="t">
                        [{{ t }}]
                      </option>
                    </select>
                    <button
                      class="tag-apply-btn"
                      type="button"
                      @click="ensureTagPrefix"
                    >
                      {{ t(`${BASE_KEY}.ACTIONS.APPLY_TO_QUESTION`) }}
                    </button>
                    <button
                      v-if="canReplaceTag"
                      class="tag-apply-btn"
                      type="button"
                      @click="replaceTagPrefix"
                    >
                      {{ t(`${BASE_KEY}.ACTIONS.REPLACE_TAG`) }}
                    </button>
                  </div>
                </div>

                <div class="form-group">
                  <label>{{ t(`${BASE_KEY}.ACTIONS.INSERT_TEMPLATE`) }}</label>
                  <div class="flex flex-wrap items-center gap-2">
                    <button
                      class="skeleton-btn"
                      type="button"
                      @click="insertAnswerTemplate('th')"
                    >
                      {{ t(`${BASE_KEY}.ACTIONS.INSERT_TH_TEMPLATE`) }}
                    </button>
                    <button
                      class="skeleton-btn"
                      type="button"
                      @click="insertAnswerTemplate('en')"
                    >
                      {{ t(`${BASE_KEY}.ACTIONS.INSERT_EN_TEMPLATE`) }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="cancel-btn" @click="handleCancel">
              {{ t('AILAB.FORM.CANCEL') }}
            </button>
            <button class="save-btn" :disabled="!canSaveDraft" @click="handleSave">
              <span class="i-lucide-save size-4" />
              {{
                editingItem ? t('AILAB.FORM.UPDATE') : t('AILAB.FORM.SAVE')
              }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Delete confirmation modal -->
    <Teleport v-if="showDeleteConfirmModal" to="body">
      <div class="modal-overlay" @click.self="closeDeleteConfirm">
        <div class="modal-content modal-content--confirm">
          <div class="modal-header">
            <h3>{{ t(`${BASE_KEY}.DELETE_MODAL.TITLE`) }}</h3>
            <button class="close-btn" @click="closeDeleteConfirm">
              <span class="i-lucide-x size-5" />
            </button>
          </div>
          <div class="modal-body">
            <div class="text-sm text-slate-700">
              {{ t(`${BASE_KEY}.DELETE_MODAL.DESCRIPTION`) }}
            </div>
          </div>
          <div class="modal-footer">
            <button class="cancel-btn" @click="closeDeleteConfirm">
              {{ t('AILAB.FORM.CANCEL') }}
            </button>
            <button
              class="save-btn"
              style="background: rgba(239, 68, 68, 0.95)"
              @click="confirmDelete"
            >
              <span class="i-lucide-trash-2 size-4" />
              {{ t('AILAB.FORM.DELETE') }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <div v-else-if="false" class="modal-overlay" @click.self="handleCancel">
      <div class="modal-content">
        <div class="modal-header">
          <h3>
            {{
              editingItem
                ? t(`${BASE_KEY}.MODAL.EDIT_TITLE`)
                : t(`${BASE_KEY}.MODAL.ADD_TITLE`)
            }}
          </h3>
          <button class="close-btn" @click="handleCancel">
            <span class="i-lucide-x size-5" />
          </button>
        </div>
        <div class="modal-body">
          <div v-if="isAIGamer" class="form-group">
            <label>{{ t(`${BASE_KEY}.FIELDS.LANGUAGE.LABEL`) }}</label>
            <select v-model="selectedLanguage" class="tag-select">
              <option
                v-for="opt in answerLanguages"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
            <p class="field-help">
              {{ t(`${BASE_KEY}.FIELDS.LANGUAGE.NOTE_DEFAULT`) }}
            </p>
          </div>
          <div v-if="isAIGamer" class="tag-row">
            <div class="tag-left">
              <label class="tag-label">{{
                t(`${BASE_KEY}.FIELDS.TAG.LABEL`)
              }}</label>
              <select
                v-model="selectedTag"
                class="tag-select"
                @change="ensureTagPrefix"
              >
                <option v-for="t in faqTags" :key="t" :value="t">
                  [{{ t }}]
                </option>
              </select>
              <button
                class="tag-apply-btn"
                type="button"
                @click="ensureTagPrefix"
              >
                {{ t(`${BASE_KEY}.ACTIONS.APPLY_TO_QUESTION`) }}
              </button>
              <button
                v-if="canReplaceTag"
                class="tag-apply-btn"
                type="button"
                @click="replaceTagPrefix"
              >
                {{ t(`${BASE_KEY}.ACTIONS.REPLACE_TAG`) }}
              </button>
            </div>
            <div class="tag-right">
              <button
                class="skeleton-btn"
                type="button"
                @click="insertAnswerTemplate('th')"
              >
                {{ t(`${BASE_KEY}.ACTIONS.INSERT_TH_TEMPLATE`) }}
              </button>
              <button
                class="skeleton-btn"
                type="button"
                @click="insertAnswerTemplate('en')"
              >
                {{ t(`${BASE_KEY}.ACTIONS.INSERT_EN_TEMPLATE`) }}
              </button>
            </div>
          </div>
          <div class="form-group">
            <label>{{
              isAIGamer
                ? t(`${BASE_KEY}.FIELDS.QUESTION.LABEL_AIGAMER`)
                : t(`${BASE_KEY}.FIELDS.QUESTION.LABEL_DEFAULT`)
            }}</label>
            <textarea
              v-model="newItem.question"
              class="form-textarea"
              :placeholder="
                isAIGamer
                  ? t(`${BASE_KEY}.FIELDS.QUESTION.PLACEHOLDER_AIGAMER`)
                  : t(`${BASE_KEY}.FIELDS.QUESTION.PLACEHOLDER_DEFAULT`)
              "
              rows="3"
              @blur="ensureTagPrefix"
            />
            <p v-if="isAIGamer" class="field-help">
              {{ t(`${BASE_KEY}.GUIDE.RECOMMENDED_TAGS_PREFIX`) }}
              [PROMO_TERMS], [TURNOVER], [ELIGIBILITY], [WITHDRAW], [DEPOSIT],
              [CANNOT_CLAIM], [DISQUALIFY], [PROVIDERS], [COMMON_ISSUES],
              [CONTACT]
            </p>
          </div>
          <div class="form-group">
            <label>{{
              isAIGamer
                ? t(`${BASE_KEY}.FIELDS.ANSWER.LABEL_AIGAMER`)
                : t(`${BASE_KEY}.FIELDS.ANSWER.LABEL_DEFAULT`)
            }}</label>
            <textarea
              v-model="newItem.answer"
              class="form-textarea"
              :placeholder="
                isAIGamer
                  ? t(`${BASE_KEY}.FIELDS.ANSWER.PLACEHOLDER_AIGAMER`)
                  : t(`${BASE_KEY}.FIELDS.ANSWER.PLACEHOLDER_DEFAULT`)
              "
              rows="5"
            />
            <p v-if="isAIGamer" class="field-help">
              {{ t(`${BASE_KEY}.GUIDE.ANSWER_CONSISTENCY_NOTE`) }}
            </p>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="handleCancel">
            {{ t('AILAB.FORM.CANCEL') }}
          </button>
          <button class="save-btn" :disabled="!canSaveDraft" @click="handleSave">
            <span class="i-lucide-save size-4" />
            {{
              editingItem ? t('AILAB.FORM.UPDATE') : t('AILAB.FORM.SAVE')
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.answer-template-tab {
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  padding-bottom: 80px;
  --answer-action-bg: #ffffff;
  --answer-action-text: #000000;
  --answer-action-hover-bg: #e5e7eb;
  --answer-action-shadow: 0 2px 8px rgba(255, 255, 255, 0.1);
  --answer-fab-shadow: 0 6px 20px rgba(0, 102, 255, 0.4);
  --answer-fab-hover-shadow: 0 8px 24px rgba(0, 102, 255, 0.5);
  --answer-surface-hover: rgba(255, 255, 255, 0.06);
  --answer-close-hover: rgba(255, 255, 255, 0.1);
}

:global(body:not(.dark) .answer-template-tab) {
  --answer-action-bg: #0066ff;
  --answer-action-text: #ffffff;
  --answer-action-hover-bg: linear-gradient(135deg, #3388ff 0%, #0066ff 100%);
  --answer-action-shadow: 0 2px 8px rgba(0, 102, 255, 0.35);
  --answer-fab-shadow: 0 6px 20px rgba(0, 102, 255, 0.3);
  --answer-fab-hover-shadow: 0 8px 24px rgba(0, 102, 255, 0.4);
  --answer-surface-hover: rgba(15, 23, 42, 0.06);
  --answer-close-hover: rgba(15, 23, 42, 0.08);
}

/* Toolbar */
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 10px;
}

.toolbar-left {
  display: flex;
  gap: 10px;
}

.import-btn,
.export-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  background: var(--answer-action-bg);
  border: none;
  border-radius: 8px;
  color: var(--answer-action-text);
  cursor: pointer;
  transition: all 0.2s;
}

.import-btn:hover,
.export-btn:hover {
  background: var(--answer-action-hover-bg);
  box-shadow: var(--answer-action-shadow);
}

.count-badge {
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  background: var(--wlab-bg-input);
  border: 1px solid var(--wlab-border-default);
  border-radius: 20px;
  color: var(--wlab-text-secondary);
}

/* AI gamer FAQ guide */
.faq-guide {
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 12px;
  padding: 14px 16px;
}

.legacy-notice {
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: 12px;
  padding: 14px 16px;
}

.legacy-notice-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--wlab-text-primary);
  margin-bottom: 6px;
}

.legacy-notice-text {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--wlab-text-secondary);
}

.legacy-notice-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 10px;
  font-size: 12px;
  color: var(--wlab-text-secondary);
}

.compat-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.compat-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--wlab-text-secondary);
}

.compat-toggle input {
  accent-color: #f59e0b;
}

.compat-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #92400e;
  background: rgba(251, 191, 36, 0.18);
  border: 1px solid rgba(245, 158, 11, 0.32);
  transition: all 0.2s ease;
}

.compat-action-btn:hover:not(:disabled) {
  background: rgba(251, 191, 36, 0.26);
}

.compat-action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.compat-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #92400e;
  background: rgba(251, 191, 36, 0.18);
  border: 1px solid rgba(245, 158, 11, 0.32);
}

.faq-guide-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--wlab-text-primary);
  margin-bottom: 8px;
}

.faq-guide-list {
  margin: 0;
  padding-left: 18px;
  color: var(--wlab-text-secondary);
  font-size: 13px;
  line-height: 1.4;
}

/* Q&A Container */
.qa-container {
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 12px;
  padding: 16px;
}

/* Q&A Table */
.qa-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.qa-table th {
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 600;
  color: var(--wlab-text-muted);
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid var(--wlab-border-light);
}

.qa-table td {
  padding: 14px 16px;
  vertical-align: top;
  border-bottom: 1px solid var(--wlab-border-light);
  overflow-wrap: anywhere;
  word-break: break-word;
}

.col-question {
  width: 38%;
}

.col-answer {
  width: auto;
}

.col-action {
  width: 96px;
  text-align: center;
}

.question-bubble {
  background: rgba(59, 130, 246, 0.15);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 12px 12px 12px 0;
  padding: 12px 16px;
}

.question-bubble p {
  margin: 0;
  font-size: 14px;
  color: var(--wlab-text-primary);
  line-height: 1.5;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.answer-bubble {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(0, 102, 255, 0.15);
  border-radius: 12px 12px 0 12px;
  padding: 12px 16px;
}

.answer-bubble p {
  margin: 0;
  font-size: 14px;
  color: var(--wlab-text-primary);
  line-height: 1.5;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.action-btns {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.action-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn.edit {
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
}

.action-btn.edit:hover {
  background: rgba(34, 197, 94, 0.2);
}

.action-btn.delete {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.action-btn.delete:hover {
  background: rgba(239, 68, 68, 0.2);
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.empty-state p {
  margin: 16px 0;
  font-size: 14px;
  color: var(--wlab-text-muted);
}

.add-first-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  background: var(--answer-action-bg);
  border: none;
  border-radius: 10px;
  color: var(--answer-action-text);
  cursor: pointer;
  transition: all 0.2s ease;
}

.add-first-btn:hover {
  background: var(--answer-action-hover-bg);
  box-shadow: var(--answer-action-shadow);
}

/* FAB Button */
.fab-btn {
  position: fixed;
  bottom: 32px;
  right: 32px;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--answer-action-bg);
  border: none;
  border-radius: 50%;
  color: var(--answer-action-text);
  cursor: pointer;
  box-shadow: var(--answer-fab-shadow);
  z-index: 100;
  transition: all 0.2s;
}

.fab-btn:hover {
  transform: scale(1.05);
  box-shadow: var(--answer-fab-hover-shadow);
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5000;
}

.modal-content {
  width: 100%;
  max-width: 1100px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
}

.modal-content--confirm {
  max-width: 520px;
}

.modal-body--wide {
  max-height: 72vh;
  overflow: auto;
}

.modal-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

.modal-main {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.modal-side {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

@media (min-width: 900px) {
  .modal-grid {
    grid-template-columns: 2fr 1fr;
    align-items: start;
  }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
}

.close-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  border: none;
  border-radius: 6px;
  color: #334155;
  cursor: pointer;
}

.close-btn:hover {
  background: #f1f5f9;
  color: #0f172a;
}

.modal-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
}

.field-help {
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
  margin-top: 2px;
}

.tag-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 12px;
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 10px;
}

.tag-left {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.tag-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.tag-label {
  font-size: 12px;
  font-weight: 700;
  color: var(--wlab-text-secondary);
}

.tag-select {
  padding: 10px 12px;
  font-size: 13px;
  color: var(--wlab-text-primary);
  background: var(--wlab-bg-input);
  border: 1px solid var(--wlab-border-default);
  border-radius: 8px;
}

/* Dark theme fix: select dropdowns should not be white-on-white */
:global(body.dark .tag-select) {
  background: rgba(0, 0, 0, 0.35);
  color: #ffffff;
  border-color: rgba(255, 255, 255, 0.12);
}

:global(body.dark select.tag-select option) {
  background: #111827;
  color: #ffffff;
}

.tag-apply-btn,
.skeleton-btn {
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  background: transparent;
  border: 1px solid var(--wlab-border-default);
  border-radius: 8px;
  color: var(--wlab-text-secondary);
  cursor: pointer;
}

.tag-apply-btn:hover,
.skeleton-btn:hover {
  background: var(--answer-surface-hover);
}

.form-textarea {
  width: 100%;
  padding: 12px 14px;
  font-size: 14px;
  color: var(--wlab-text-primary);
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-default);
  border-radius: 8px;
  resize: vertical;
}

.form-textarea:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.2);
}

.form-textarea::placeholder {
  color: var(--wlab-text-muted);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 20px;
  border-top: 1px solid var(--wlab-border-light);
}

.cancel-btn {
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  background: transparent;
  border: 1px solid var(--wlab-border-default);
  border-radius: 8px;
  color: var(--wlab-text-secondary);
  cursor: pointer;
}

.save-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  background: var(--answer-action-bg);
  border: none;
  border-radius: 8px;
  color: var(--answer-action-text);
  cursor: pointer;
}

.save-btn:hover {
  background: var(--answer-action-hover-bg);
  box-shadow: var(--answer-action-shadow);
}
</style>
